/**
 * Resource Type Timeline Chart
 *
 * Stacked bar chart showing daily interactions broken down by resource type:
 * - Links (amber/orange)
 * - QR Codes (purple)
 * - Pages (blue)
 *
 * @module components/charts/resource-type-timeline-chart
 */

import { useMemo, memo } from 'react';
import { subDays, eachDayOfInterval, format } from 'date-fns';
import { formatInTimeZone, toZonedTime } from 'date-fns-tz';
import { Bar, BarChart, XAxis, YAxis, CartesianGrid } from 'recharts';
import { Link2, QrCode, FileText, BarChart2 } from 'lucide-react';
import {
  type ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from '@/client/components/ui/chart';
import { BAR_MAX_SIZE, CHART_HEIGHTS, CHART_MARGINS } from '@/client/lib/chart-constants';
import { useTimezone } from '@/client/hooks/use-timezone';

interface TimelineDataPoint {
  date: string;
  interactions: number;
  byResourceType?: {
    link: number;
    qr: number;
    page: number;
  };
}

interface ResourceTypeTimelineChartProps {
  timeline: TimelineDataPoint[];
  days?: number;
}

// Fixed colors for each resource type
const RESOURCE_COLORS = {
  link: 'hsl(38 92% 50%)', // amber/orange
  qr: 'hsl(271 81% 56%)', // purple
  page: 'hsl(221 83% 53%)', // blue
} as const;

const chartConfig: ChartConfig = {
  link: {
    label: 'Links',
    color: RESOURCE_COLORS.link,
  },
  qr: {
    label: 'QR Codes',
    color: RESOURCE_COLORS.qr,
  },
  page: {
    label: 'Pages',
    color: RESOURCE_COLORS.page,
  },
};

export const ResourceTypeTimelineChart = memo(function ResourceTypeTimelineChart({
  timeline,
  days = 14,
}: ResourceTypeTimelineChartProps) {
  const { effectiveTimezone } = useTimezone();
  const timezone = effectiveTimezone || Intl.DateTimeFormat().resolvedOptions().timeZone;

  const { chartData, totals } = useMemo(() => {
    // Check if we are dealing with hourly data
    const isHourly = timeline.length > 0 && timeline[0]?.date.includes(':');

    if (isHourly) {
      // For hourly data, use the data points directly
      let totalLink = 0;
      let totalQr = 0;
      let totalPage = 0;

      const data = timeline.map((point) => {
        const dateObj = new Date(point.date.replace(' ', 'T'));
        if (point.byResourceType) {
          totalLink += point.byResourceType.link || 0;
          totalQr += point.byResourceType.qr || 0;
          totalPage += point.byResourceType.page || 0;
        }
        return {
          date: point.date,
          displayDate: formatInTimeZone(dateObj, timezone, 'h a'),
          link: point.byResourceType?.link || 0,
          qr: point.byResourceType?.qr || 0,
          page: point.byResourceType?.page || 0,
        };
      });

      return {
        chartData: data,
        totals: {
          link: totalLink,
          qr: totalQr,
          page: totalPage,
          total: totalLink + totalQr + totalPage,
        },
      };
    }

    // Daily logic
    const now = new Date();
    const todayInTz = toZonedTime(now, timezone);
    const endDate = todayInTz;
    const startDate = subDays(endDate, days - 1);

    // Create a map of all days in the range using LOCAL dates
    const dayMap = new Map<
      string,
      { date: string; link: number; qr: number; page: number; displayDate?: string }
    >();

    eachDayOfInterval({ start: startDate, end: endDate }).forEach((date) => {
      const key = format(date, 'yyyy-MM-dd');
      dayMap.set(key, { date: key, link: 0, qr: 0, page: 0 });
    });

    // Fill in data
    let totalLink = 0;
    let totalQr = 0;
    let totalPage = 0;

    timeline.forEach((point) => {
      const localDateKey = point.date;
      const day = dayMap.get(localDateKey);
      if (day && point.byResourceType) {
        day.link += point.byResourceType.link || 0;
        day.qr += point.byResourceType.qr || 0;
        day.page += point.byResourceType.page || 0;
        totalLink += point.byResourceType.link || 0;
        totalQr += point.byResourceType.qr || 0;
        totalPage += point.byResourceType.page || 0;
      }
    });

    const data = Array.from(dayMap.values()).map((day) => ({
      ...day,
      displayDate: formatInTimeZone(new Date(`${day.date}T12:00:00`), timezone, 'MMM d'),
    }));

    return {
      chartData: data,
      totals: {
        link: totalLink,
        qr: totalQr,
        page: totalPage,
        total: totalLink + totalQr + totalPage,
      },
    };
  }, [timeline, days, timezone]);

  // Check if we have any resource type breakdown data
  const hasResourceTypeData = timeline.some((p) => p.byResourceType);

  if (timeline.length === 0) {
    return (
      <div className="h-48 flex flex-col items-center justify-center text-muted-foreground">
        <BarChart2 className="size-8 mb-2 text-muted-foreground/50" />
        <p className="text-sm">No activity data available</p>
        <p className="text-xs text-muted-foreground/70 mt-1">
          Resource type breakdown will appear here
        </p>
      </div>
    );
  }

  if (!hasResourceTypeData) {
    return (
      <div className="h-48 flex flex-col items-center justify-center text-muted-foreground">
        <BarChart2 className="size-8 mb-2 text-muted-foreground/50" />
        <p className="text-sm">Resource type breakdown not available</p>
        <p className="text-xs text-muted-foreground/70 mt-1">
          Try selecting a different time period
        </p>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h4 className="text-sm font-semibold text-muted-foreground">Activity by Resource Type</h4>
        <span className="text-sm font-medium text-muted-foreground">
          {totals.total.toLocaleString()} total
        </span>
      </div>

      <ChartContainer
        config={chartConfig}
        className="w-full"
        style={{ height: CHART_HEIGHTS.medium }}
      >
        <BarChart data={chartData} margin={CHART_MARGINS.vertical}>
          <CartesianGrid
            vertical={false}
            strokeDasharray="3 3"
            stroke="var(--border)"
            opacity={0.4}
          />
          <XAxis
            dataKey="displayDate"
            tickLine={false}
            axisLine={{ stroke: 'var(--border)', strokeWidth: 1 }}
            tickMargin={8}
            fontSize={10}
            minTickGap={30}
          />
          <YAxis
            tickLine={false}
            axisLine={{ stroke: 'var(--border)', strokeWidth: 1 }}
            tickMargin={4}
            fontSize={10}
            width={40}
            tickFormatter={(value) => (value === 0 ? '' : value)}
          />
          <ChartTooltip
            content={
              <ChartTooltipContent
                labelFormatter={(value) => value}
                formatter={(value, name) => {
                  const config = chartConfig[name as keyof typeof chartConfig];
                  return (
                    <div className="flex items-center gap-2">
                      <div
                        className="h-2.5 w-2.5 rounded-sm shrink-0"
                        style={{ backgroundColor: config?.color }}
                      />
                      <span className="text-muted-foreground">{config?.label}</span>
                      <span className="font-mono font-medium tabular-nums ml-auto">
                        {(value as number)?.toLocaleString()}
                      </span>
                    </div>
                  );
                }}
              />
            }
          />
          {/* Stacked bars - order determines visual stacking (bottom to top) */}
          <Bar
            dataKey="link"
            stackId="resourceType"
            fill={RESOURCE_COLORS.link}
            radius={[0, 0, 0, 0]}
            maxBarSize={BAR_MAX_SIZE}
          />
          <Bar
            dataKey="qr"
            stackId="resourceType"
            fill={RESOURCE_COLORS.qr}
            radius={[0, 0, 0, 0]}
            maxBarSize={BAR_MAX_SIZE}
          />
          <Bar
            dataKey="page"
            stackId="resourceType"
            fill={RESOURCE_COLORS.page}
            radius={[4, 4, 0, 0]}
            maxBarSize={BAR_MAX_SIZE}
          />
        </BarChart>
      </ChartContainer>

      {/* Legend */}
      <div className="mt-4 flex flex-wrap items-center gap-4">
        <div className="flex items-center gap-1.5">
          <div
            className="w-3 h-3 rounded-sm shrink-0"
            style={{ backgroundColor: RESOURCE_COLORS.link }}
          />
          <Link2 className="size-3 text-muted-foreground" />
          <span className="text-xs text-muted-foreground">Links</span>
          <span className="text-xs font-medium">{totals.link.toLocaleString()}</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div
            className="w-3 h-3 rounded-sm shrink-0"
            style={{ backgroundColor: RESOURCE_COLORS.qr }}
          />
          <QrCode className="size-3 text-muted-foreground" />
          <span className="text-xs text-muted-foreground">QR Codes</span>
          <span className="text-xs font-medium">{totals.qr.toLocaleString()}</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div
            className="w-3 h-3 rounded-sm shrink-0"
            style={{ backgroundColor: RESOURCE_COLORS.page }}
          />
          <FileText className="size-3 text-muted-foreground" />
          <span className="text-xs text-muted-foreground">Pages</span>
          <span className="text-xs font-medium">{totals.page.toLocaleString()}</span>
        </div>
      </div>
    </div>
  );
});
